import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Container, FormControlLabel,
  IconButton, LinearProgress, Paper, Skeleton, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderIcon from '@mui/icons-material/Folder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import profileApi from '../Profile/profileApi';
import eventApi from '../EventGallery/eventApi';
import { waitForGroupingCompletion, promptAutoSyncIncludeExisting, promptStopOtherAutoSync, GroupingCancelledError } from '../EventGallery/EventPage';
import { useLocalAutoSync } from '../../utils/localFolderSync/useLocalAutoSync';
import { swalConsentCheckbox, swalConfirmDelete } from '../../utils/swal';
import { Capacitor } from '@capacitor/core';
import { nativeUploadService, makeUploadJobId } from '../../utils/backgroundUpload/nativeUploadService';
import { useNativeUploadObserver } from '../../utils/backgroundUpload/useNativeUploadObserver';
import {
  UPLOAD_IN_PROGRESS_WARNING, UPLOAD_BACKGROUNDABLE_NOTICE, shouldShowUploadWarning, shouldShowBackgroundableNotice,
} from '../../utils/backgroundUpload/uploadCloseWarning';
import uploadConstants from '../../config/uploadConstants.json';
import StrandedAutoSyncStopButton from '../../components/common/StrandedAutoSyncStopButton';

const LOCK_BUSY_RETRY_MS = uploadConstants.GROUPING_LOCK_RETRY_MS;
const SUBMISSIONS_PAGE_SIZE = 20;
const NATIVE_JOB_WATCH_INTERVAL_MS = 1500;

const UPLOADER_GROUPING_CONSENT_HTML = `
  <div style="text-align:left;font-size:0.92rem;line-height:1.55;">
    <p>The photos you upload here will be processed using biometric face-detection and face-matching technology to organise them into the event gallery.</p>
    <p>This processing is carried out under the <strong>event organiser's</strong> policies and authority - by continuing, you confirm you are authorised by the organiser to submit these photos for this purpose, and agree to CircleMoments' <a href="/terms.html" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</p>
  </div>
`;
const UPLOADER_GROUPING_CONSENT_CHECKBOX_LABEL = "I confirm I'm authorised by the organiser to submit these photos for biometric face-grouping.";

// Distinct wording from the organiser-facing consents (confirmFaceGroupingConsent,
// confirmUploadLinkGroupingConsent in EventPage.js) - this uploader isn't the
// event organiser and isn't necessarily the subject of the photos either; what
// they're attesting is narrower: that the organiser has authorised them to
// submit photos for biometric processing under the organiser's own policies.
async function confirmUploaderGroupingConsent() {
  return swalConsentCheckbox({
    title: 'Biometric Processing Consent',
    html: UPLOADER_GROUPING_CONSENT_HTML,
    checkboxLabel: UPLOADER_GROUPING_CONSENT_CHECKBOX_LABEL,
  });
}
// How long an anonymous visitor's device is remembered before they have to
// re-enter their details (and effectively start a new session) - refreshed
// on every successful reuse, so an active repeat submitter is never cut off
// mid-use; a device that stops coming back goes quiet after this long.
const SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function sessionStorageKey(token) {
  return `cm-upload-link-session:${token}`;
}

// Returns the persisted {name, email, phone, sessionId} for this link on this
// device, or null if there's none or it's past SESSION_TTL_MS since it was
// last used (see confirmDetails, which re-saves createdAt on every load to
// slide the window forward).
export function loadStoredSession(token) {
  try {
    const raw = JSON.parse(localStorage.getItem(sessionStorageKey(token)) || 'null');
    if (!raw || !raw.sessionId || !raw.createdAt) return null;
    if (Date.now() - raw.createdAt > SESSION_TTL_MS) return null;
    return raw;
  } catch {
    return null;
  }
}

function saveStoredSession(token, session) {
  try {
    localStorage.setItem(sessionStorageKey(token), JSON.stringify({ ...session, createdAt: Date.now() }));
  } catch { /* localStorage unavailable (private mode etc.) - session just won't persist */ }
}

function pendingGroupingStorageKey(token) {
  return `cm-upload-link-pending-grouping:${token}`;
}

// Submission_ids uploaded but not yet successfully grouped, persisted so a
// mid-attempt page refresh doesn't silently lose track of them (see
// setPendingGroupingIds) - the in-memory retry loop in triggerGroupingWithRetry
// can't survive a refresh, but this list can, and gets swept into whatever
// grouping attempt runs next.
function loadPendingGroupingIds(token) {
  try {
    const raw = JSON.parse(localStorage.getItem(pendingGroupingStorageKey(token)) || 'null');
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

function savePendingGroupingIds(token, ids) {
  try {
    if (ids.length) {
      localStorage.setItem(pendingGroupingStorageKey(token), JSON.stringify(ids));
    } else {
      localStorage.removeItem(pendingGroupingStorageKey(token));
    }
  } catch { /* localStorage unavailable (private mode etc.) - list just won't survive a refresh */ }
}

function newSessionId() {
  return window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// Shown while useLocalAutoSync is reading a folder (initial pick, or a
// background re-poll of a very large one) - a folder with thousands of
// photos can take a long time, so this gives real feedback plus a cancel
// button instead of the app silently appearing to do nothing for that action
// while staying interactive everywhere else.
function ScanProgressBar({ autoSync }) {
  if (!autoSync.scanning) return null;
  const { phase, completed, total } = autoSync.scanProgress || {};
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <LinearProgress
          variant={total ? 'determinate' : 'indeterminate'}
          value={total ? (completed / total) * 100 : undefined}
          sx={{ borderRadius: 1, height: 6 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {phase === 'scanning'
            ? `Scanning folder… ${completed || 0} photo${completed === 1 ? '' : 's'} found so far`
            : `Reading photos… ${completed || 0}/${total || 0}`}
        </Typography>
      </Box>
      <Tooltip title="Cancel">
        <span>
          <IconButton size="small" onClick={autoSync.cancelScan} disabled={autoSync.scanCancelRequested}>
            {autoSync.scanCancelRequested ? <CircularProgress size={16} /> : <CloseIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}

// Native (CmUploadWorker/WorkManager) doesn't hand back a compress/upload
// progress callback the way the browser upload path does - `job` (from
// useNativeUploadObserver) is the only window into what's actually happening,
// polled every ~2.5s. Without this, the page had no way to reflect that same
// progress: the manual "Upload" button's busy state and auto-sync's own
// pendingCount both flip back to their resting state the moment the job is
// handed off to WorkManager (which returns almost immediately - the real
// compress/upload work continues after that), so the only visible sign of
// activity used to be pendingCount quietly dropping straight to 0 with no
// feedback in between, even though the OS notification (fed by
// UploadNotifications.updateProgress) was counting up the whole time.
function NativeUploadProgress({ job, onCancel }) {
  if (!job) return null;
  const phaseLabel = { compressing: 'Compressing photos…', uploading: 'Uploading photos…', processing: 'Finalising upload…' }[job.phase] || 'Uploading…';
  const total = job.totalFiles || 0;
  const completed = job.completedFiles || 0;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="body2" fontWeight={700}>{phaseLabel}</Typography>
        {onCancel && <Button size="small" color="error" onClick={onCancel} sx={{ textTransform: 'none' }}>Cancel</Button>}
      </Stack>
      <LinearProgress
        variant={total > 0 ? 'determinate' : 'indeterminate'}
        value={total > 0 ? Math.round((completed / total) * 100) : undefined}
        sx={{ borderRadius: 1, height: 6 }}
      />
      {total > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {completed}/{total} photos
        </Typography>
      )}
    </Box>
  );
}

// Retries a grouping-trigger call when the backend marks the error retryable
// (a "job already in progress" 409 - see grouping.py/event_management.py's
// `retryable: true` field) rather than treating it as a terminal failure. The
// lock is per-event, shared across every organiser tab and every visitor of
// every upload link for this event, so a queued attempt is expected to
// eventually succeed once whichever job is running finishes. Other 409s (e.g.
// a permanent business-rule violation) are NOT retryable and propagate as-is.
//
// `signal`, if given and aborted, throws GroupingCancelledError instead of
// continuing to retry - this phase hasn't actually reserved anything server-
// side yet (every retryable 409 means *this* attempt's initiate-grouping call
// failed), so cancelling here is purely local: nothing to tell the server to
// stop, since the currently-active job belongs to someone else entirely.
export async function triggerGroupingWithRetry(eventId, submissionIds, mode, token, onQueued, signal) {
  console.log(`[UploadLink] triggerGroupingWithRetry: event=${eventId} mode=${mode} submissions=${submissionIds.length}`);
  let lockRetries = 0;
  for (;;) {
    if (signal?.aborted) {
      console.log(`[UploadLink] triggerGroupingWithRetry: event=${eventId} aborted after ${lockRetries} lock retries`);
      throw new GroupingCancelledError();
    }
    try {
      const result = await eventApi.groupSubmittedPhotosViaLink(eventId, submissionIds, mode, token);
      console.log(`[UploadLink] triggerGroupingWithRetry: event=${eventId} grouping triggered (after ${lockRetries} lock retries)`);
      return result;
    } catch (err) {
      if (err?.data?.retryable) {
        lockRetries += 1;
        console.log(`[UploadLink] triggerGroupingWithRetry: event=${eventId} event lock busy (retry #${lockRetries}), waiting ${LOCK_BUSY_RETRY_MS}ms`);
        onQueued?.();
        await new Promise((resolve, reject) => {
          const timer = setTimeout(resolve, LOCK_BUSY_RETRY_MS);
          signal?.addEventListener('abort', () => { clearTimeout(timer); reject(new GroupingCancelledError()); }, { once: true });
        });
        continue;
      }
      console.error(`[UploadLink] triggerGroupingWithRetry: event=${eventId} FAILED:`, err?.message || err);
      throw err;
    }
  }
}

// Passed as useLocalAutoSync's onReattach for the guest/upload-link flow's own
// foreground mount-reattach (e.g. the app was killed mid-upload while this
// page was open, then reopened). Closed-app/headless auto-sync used to also
// reattach through this same path (SyncWorkerPage.js's FolderSyncWorker, since
// migrated onto the native WorkManager pipeline - see AutoSyncTrigger.kt).
// Without ANY onReattach given, useLocalAutoSync treats a mid-flight batch
// found on mount as an unverified, automatic SUCCESS - the bug that let a
// batch stuck/interrupted mid-compression (or mid-upload) get silently marked
// "delivered" and its pending count reset to 0 the moment the page next
// mounted, even though nothing had actually reached S3.
//
// Unlike the organiser flow's own onReattach (reattachIfJobInFlight in
// EventPage.js), which can verify a genuinely still-in-flight batch's real
// outcome via grouping-status polling, a plain photo-submission batch has no
// server-side "job" to poll - relativePath-keyed local files have no
// reliable mapping back to already-submitted photos. Throwing here forces
// useLocalAutoSync's reattach path to requeue the batch for a fresh retry
// instead: occasionally re-uploading a batch that actually did finish just
// before the interruption is a far safer failure mode than silently marking
// real, un-uploaded photos as delivered.
export async function requeueOnUploadLinkReattach() {
  console.log('[UploadLink] requeueOnUploadLinkReattach: mid-flight batch found on mount, cannot verify - requeuing for retry');
  throw new Error('Cannot verify a mid-flight upload-link batch - requeuing for retry.');
}

export default function UploadLinkPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [detailsConfirmed, setDetailsConfirmed] = useState(false);
  // Anonymous-session id, persisted client-side (see loadStoredSession) so a
  // returning-within-7-days visitor's own submissions can be found again after
  // a refresh. Unused (left '') for a logged-in visitor - customer_id already
  // identifies them server-side.
  const [sessionId, setSessionId] = useState('');

  const [manualFiles, setManualFiles] = useState([]);
  const [manualBusy, setManualBusy] = useState(false);
  const [manualProgress, setManualProgress] = useState('');
  // Whether the manual "Upload" button also triggers grouping - defaults off
  // so a visitor who never touches this switch doesn't unexpectedly kick off
  // a billed grouping run. Only rendered/relevant when canGroup; a plain
  // upload-only link never shows it and never groups regardless of this value.
  const [alsoGroup, setAlsoGroup] = useState(false);
  // { phase: 'compressing'|'uploading', completed, total } while manualBusy,
  // fed by uploadPhotoSubmissionsViaLink's onProgress - drives the determinate
  // progress bar under the Upload button.
  const [uploadProgress, setUploadProgress] = useState(null);
  // Reconciliation for a manual upload that was still running (native only)
  // when this page was last closed - reflects reality (read-only) rather than
  // letting the user start a duplicate upload while the headless resume (see
  // FolderSyncForegroundService's onTaskRemoved) is still finishing it.
  const [resumeJob, setResumeJob] = useState(null);
  // True from the moment a grouping-trigger flow starts (either this session's
  // own upload/auto-sync batch, or a job discovered already running on mount -
  // see the reattach effect below) until it resolves - drives the cancel
  // button, since either origin is equally cancellable.
  const [groupingActive, setGroupingActive] = useState(false);
  const [groupingQueued, setGroupingQueued] = useState(false);
  const [groupingDone, setGroupingDone] = useState(false);
  const [groupingCancelled, setGroupingCancelled] = useState(false);
  const [snackError, setSnackError] = useState('');
  const [mySubmissions, setMySubmissions] = useState([]);
  // Renders the "Your uploads" thumbnail grid in pages of 20 - a heavy
  // auto-sync session can accumulate thousands of submissions, and mounting
  // that many <img> elements at once (even with loading="lazy") is a real
  // rendering cost on the low-end devices this page targets. Grows via
  // "Load more"; never shrinks back down when new uploads arrive.
  const [visibleSubmissionsCount, setVisibleSubmissionsCount] = useState(SUBMISSIONS_PAGE_SIZE);
  // Presigned thumbnail URLs for "Your uploads", batch-fetched (never one
  // request per photo - see getUploadLinkSubmissionPresignedUrls's comment)
  // and cached by submission_id. fetchedSubmissionIdsRef tracks in-flight/
  // already-fetched IDs so re-renders (pagination, new uploads) don't
  // re-request the same ones; an ID is removed again on failure so it's
  // eligible for retry.
  const [thumbUrls, setThumbUrls] = useState({});
  const fetchedSubmissionIdsRef = useRef(new Set());
  // Current cancellable grouping wait, if any - shared by the manual-upload
  // flow, the auto-sync flow, and the on-mount reattach check, so a single
  // cancel button/handler covers all three origins.
  const groupingAbortRef = useRef(null);
  // True once we've actually observed (via a real server response, either our
  // own successful initiate-grouping/kickoff or a reattach status check) that
  // a job is genuinely active for this event - only then is it safe for
  // "Cancel" to call the server's cancel-grouping endpoint. While merely
  // queued/retrying (getting retryable 409s), the currently-active job
  // belongs to someone else entirely, so cancelling here must stay local-only.
  const groupingConfirmedActiveRef = useRef(false);
  // submission_ids that were successfully uploaded but never got grouped -
  // because a cancel arrived (queued or active), a genuine failure occurred,
  // or the tab was refreshed before a hold was ever confirmed. The photos
  // themselves are safe (already uploaded); this is purely "still needs a
  // grouping trigger" bookkeeping, persisted to localStorage (see
  // setPendingGroupingIds) so a mid-attempt refresh can't silently drop it -
  // only cleared once a hold is actually confirmed active (the server-side
  // reattach effect takes over from there) or grouping actually completes.
  // Initialized from localStorage so a refresh mid-queue picks up where it
  // left off instead of resetting to 0.
  const pendingGroupingSubmissionIdsRef = useRef(loadPendingGroupingIds(token));
  // submission_ids the *current* runGroupingFlow call is waiting on - read by
  // handleCancelGrouping to know what to push back into the pending-regroup
  // queue above.
  const currentGroupingSubmissionIdsRef = useRef([]);
  const [pendingGroupingCount, setPendingGroupingCount] = useState(() => loadPendingGroupingIds(token).length);
  // Single point of truth for updating the pending-regroup list: keeps the
  // ref, the display count, and the localStorage copy in lockstep so none of
  // the three can drift out of sync with the others.
  const setPendingGroupingIds = useCallback((ids) => {
    pendingGroupingSubmissionIdsRef.current = ids;
    setPendingGroupingCount(ids.length);
    savePendingGroupingIds(token, ids);
  }, [token]);
  // Guards against a rapid double-click on "Cancel" double-queuing the same
  // in-flight submission_ids into pendingGroupingSubmissionIdsRef - the abort
  // signal takes a tick to actually unwind runGroupingFlow's await chain, so
  // groupingAbortRef.current can still be non-null for a moment after the
  // first click. Reset at the start of every new runGroupingFlow call.
  const groupingCancelRequestedRef = useRef(false);

  useEffect(() => {
    if (!token) { setError('No upload link provided.'); setLoading(false); return; }
    eventApi.getUploadLinkPageData(token)
      .then((d) => setData(d))
      .catch((err) => setError(err?.response?.data?.error || err?.message || 'This upload link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  // Logged-in visitor: prefill from their profile, same pattern as JoinEvent/
  // PhotoRequestFormDialog. Their JWT is attached automatically by the axios
  // interceptor on every call below, so the backend records their customer_id
  // on the resulting submission regardless of what's typed here.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setName((cur) => cur || user.customer_name || '');
    setEmail((cur) => cur || user.email || '');
    profileApi.getProfile()
      .then((profile) => {
        if (cancelled || !profile) return;
        setName((cur) => cur || profile.customer_name || '');
        setEmail((cur) => cur || profile.email || '');
        setPhone((cur) => cur || profile.phone || profile.extended?.alternate_phone || '');
      })
      .catch(() => {
        if (cancelled) return;
        setPhone((cur) => cur || user.phone || user.phonenumber || '');
      });
    return () => { cancelled = true; };
  }, [user]);

  // Anonymous visitor: reuse a still-valid (< 7 day old) session on this device
  // and skip the form, or mint a fresh session id ready for when they do
  // confirm details (or upload before doing so, if that ever becomes possible).
  useEffect(() => {
    if (user || !token) return;
    const stored = loadStoredSession(token);
    if (stored?.name) {
      setName(stored.name);
      setEmail(stored.email || '');
      setPhone(stored.phone || '');
      setSessionId(stored.sessionId);
      setConsent(true);
      setDetailsConfirmed(true);
      saveStoredSession(token, stored); // slide the 7-day window forward on reuse
    } else {
      setSessionId(newSessionId());
    }
  }, [user, token]);

  const identity = useMemo(() => ({ name: name.trim(), email: email.trim(), phone: phone.trim() }), [name, email, phone]);

  function confirmDetails(e) {
    e.preventDefault();
    if (!identity.name || !identity.email) return;
    if (!user) {
      saveStoredSession(token, { ...identity, sessionId: sessionId || newSessionId() });
    }
    setDetailsConfirmed(true);
  }

  const eventId = data?.event_id;
  // Only a logged-in CircleMoments account may trigger grouping through a link -
  // any registered account, not necessarily this event's organiser/attendee -
  // anonymous visitors can still submit photos (see submitFiles) even when the
  // link itself permits grouping. Enforced again server-side in
  // grouping_actor_required; this just keeps the UI from offering an action
  // that would otherwise 401.
  const linkAllowsGrouping = Boolean(data?.can_trigger_grouping);
  const canGroup = linkAllowsGrouping && Boolean(user);
  const groupingMode = data?.has_gallery ? 'add' : 'initial';

  const refreshMySubmissions = useCallback(() => {
    if (!eventId || (!user && !sessionId)) return;
    eventApi.getMyUploadLinkSubmissions(eventId, token, sessionId)
      .then((d) => setMySubmissions(Array.isArray(d?.submissions) ? d.submissions : []))
      .catch(() => {});
  }, [eventId, token, sessionId, user]);

  // True once this component instance unmounts - checked by watchNativeJob's
  // poll loop below so it stops scheduling further polls (and never calls
  // setState) after the page's gone, since nothing else cancels that loop on
  // its own.
  const unmountedRef = useRef(false);
  useEffect(() => () => { unmountedRef.current = true; }, []);

  // Watches a native (WorkManager) job started BY THIS component instance, by
  // its own specific jobId via getJobStatus - a tighter, more reliable
  // alternative to just waiting on the shared useNativeUploadObserver poll
  // below for THIS job. That observer only reflects a job once it happens to
  // catch it via listActiveJobs on one of its own independent ~2.5s ticks -
  // but native creates the job manifest synchronously (before startUploadJob's
  // own promise even resolves here) and drops it from listActiveJobs the
  // instant an outcome is set, so a batch that starts and finishes entirely
  // between two of the observer's ticks (routine for this page's typically
  // small batches - a single photo on a fast connection, easily) can complete
  // without the observer's `job` ever going truthy: no in-app progress ever
  // shown, no active->inactive transition to notice, nothing but the OS
  // notification the whole time. Polling this exact jobId, starting
  // immediately (not waiting out a first interval) and on a tighter cadence,
  // sidesteps that: progress shows up right away and reliably, and completion
  // is caught even for a job that would have squeezed entirely inside one gap
  // of the generic observer's own cadence.
  //
  // Also feeds `resumeJob` directly (the same state the generic observer
  // feeds) so the existing NativeUploadProgress display picks this job up
  // immediately - both writers converge on the same eventual value, so having
  // the generic observer also running concurrently is harmless.
  const watchNativeJob = useCallback(async (jobId, { onDone } = {}) => {
    for (;;) {
      const res = await nativeUploadService.getJobStatus(jobId);
      if (unmountedRef.current) return;
      if (!res?.found || res.job?.outcome) {
        setResumeJob((cur) => (cur?.jobId === jobId ? null : cur));
        onDone?.(res?.job || null);
        return;
      }
      setResumeJob(res.job);
      await new Promise((resolve) => setTimeout(resolve, NATIVE_JOB_WATCH_INTERVAL_MS));
      if (unmountedRef.current) return;
    }
  }, []);

  // Auto-sync's own native batches: no local busy/progress state of their own
  // to reconcile (their in-app progress is already purely resumeJob-driven -
  // see the auto-sync card below), so completion just needs to refresh "Your
  // uploads" - plus, when this batch deliberately didn't group (alsoGroup was
  // off), diff against a pre-batch snapshot to discover and queue whatever it
  // just uploaded, same as watchManualNativeUpload does for the manual button.
  const watchNativeJobAndRefresh = useCallback((jobId, opts = {}) => {
    const { beforeUngroupedIds } = opts;
    watchNativeJob(jobId, {
      onDone: () => {
        if (beforeUngroupedIds) {
          eventApi.getMyUploadLinkSubmissions(eventId, token, sessionId).then((d) => {
            const subs = Array.isArray(d?.submissions) ? d.submissions : [];
            setMySubmissions(subs);
            const newIds = subs.filter(s => !s.grouped && !beforeUngroupedIds.has(s.submission_id)).map(s => s.submission_id);
            if (newIds.length) setPendingGroupingIds([...pendingGroupingSubmissionIdsRef.current, ...newIds]);
          }).catch(() => {});
        } else {
          refreshMySubmissions();
        }
      },
    });
  }, [watchNativeJob, refreshMySubmissions, eventId, token, sessionId, setPendingGroupingIds]);

  // The manual "Upload"/"Upload & Group" button's own native job: keeps
  // manualBusy (and hence the button's disabled/spinner state) true for as
  // long as this job is genuinely still running - see handleManualUpload,
  // which deliberately skips its own finally-block cleanup for this path so
  // this is the only thing that clears it - instead of the previous
  // behaviour, where manualBusy (and the local compress/upload progress bar)
  // was cleared the instant the near-instantaneous native hand-off call
  // itself resolved, well before the real compress/upload work even started.
  const watchManualNativeUpload = useCallback((jobId, opts = {}) => {
    const { beforeUngroupedIds } = opts;
    watchNativeJob(jobId, {
      onDone: () => {
        setManualBusy(false);
        setManualProgress('');
        setUploadProgress(null);
        // Native uploads run headlessly in CmUploadWorker with no synchronous
        // submission_ids result - when this job deliberately didn't group
        // (willGroup was false), diff the visitor's own ungrouped submissions
        // against a snapshot taken just before it started to discover which
        // ones it just created, and queue them the same way a
        // cancelled/interrupted grouping attempt is queued.
        if (beforeUngroupedIds) {
          eventApi.getMyUploadLinkSubmissions(eventId, token, sessionId).then((d) => {
            const subs = Array.isArray(d?.submissions) ? d.submissions : [];
            setMySubmissions(subs);
            const newIds = subs.filter(s => !s.grouped && !beforeUngroupedIds.has(s.submission_id)).map(s => s.submission_id);
            if (newIds.length) setPendingGroupingIds([...pendingGroupingSubmissionIdsRef.current, ...newIds]);
          }).catch(() => {});
        } else {
          refreshMySubmissions();
        }
      },
    });
  }, [watchNativeJob, refreshMySubmissions, eventId, token, sessionId, setPendingGroupingIds]);

  // Load previously-submitted photos once the event/session are known, so a
  // refresh (or a returning-within-7-days anonymous visitor) sees what they
  // already uploaded instead of an empty page.
  useEffect(() => { refreshMySubmissions(); }, [refreshMySubmissions]);

  const ensureThumbUrls = useCallback((ids) => {
    if (!eventId) return;
    const need = ids.filter(id => id && !fetchedSubmissionIdsRef.current.has(id));
    if (!need.length) return;
    need.forEach(id => fetchedSubmissionIdsRef.current.add(id));
    eventApi.getUploadLinkSubmissionPresignedUrls(eventId, need, token, sessionId).then(res => {
      setThumbUrls(prev => ({ ...prev, ...(res.urls || {}) }));
    }).catch(() => { need.forEach(id => fetchedSubmissionIdsRef.current.delete(id)); });
  }, [eventId, token, sessionId]);

  // Batch-fetch presigned URLs for exactly the page of thumbnails currently
  // visible (not the whole upload history) - grows as "Load N more" advances
  // visibleSubmissionsCount.
  useEffect(() => {
    ensureThumbUrls(mySubmissions.slice(0, visibleSubmissionsCount).map(s => s.submission_id));
  }, [mySubmissions, visibleSubmissionsCount, ensureThumbUrls]);

  // Native-only: observes whatever CmUploadWorker (WorkManager) is already
  // doing for this event's upload-link-manual job - never resumes or re-runs
  // anything (native never stopped running it); purely reads state back for
  // the banner/progress display. See EventPage.js's matching effect.
  const nativeJob = useNativeUploadObserver(eventId, ['upload-link-manual']);
  const priorNativeJobRef = useRef(null);
  useEffect(() => {
    setResumeJob(nativeJob);
    if (!nativeJob && priorNativeJobRef.current) refreshMySubmissions();
    priorNativeJobRef.current = nativeJob;
  }, [nativeJob, refreshMySubmissions]);

  const submitFiles = useCallback(async (files, onProgress, hooks) => {
    if (!files.length || !eventId) return [];
    console.log(`[UploadLink] submitFiles: event=${eventId} files=${files.length}`);
    const res = await eventApi.uploadPhotoSubmissionsViaLink(eventId, files, token, identity, sessionId, onProgress, undefined, hooks);
    console.log(`[UploadLink] submitFiles: event=${eventId} submitted, submissionIds=${(res.submission_ids || []).length}`);
    refreshMySubmissions();
    return res.submission_ids || [];
  }, [eventId, token, identity, sessionId, refreshMySubmissions]);

  // Shared by the manual "Upload & Group" button and auto-sync's batch
  // handler (and mirrored by the reattach effect below for a job discovered
  // already running) - owns the single groupingAbortRef/groupingConfirmedActiveRef
  // pair so one cancel button/handler covers every origin uniformly. Always
  // sweeps in any submission_ids left over from a previous cancelled attempt
  // (see pendingGroupingSubmissionIdsRef) so they don't need a separate retry
  // path of their own.
  const runGroupingFlow = useCallback(async (newSubmissionIds) => {
    const submissionIds = [...pendingGroupingSubmissionIdsRef.current, ...(newSubmissionIds || [])];
    console.log(`[UploadLink] runGroupingFlow: event=${eventId} newSubmissionIds=${(newSubmissionIds || []).length} pendingCarriedOver=${pendingGroupingSubmissionIdsRef.current.length} canGroup=${canGroup}`);
    if (!canGroup || !submissionIds.length) return;
    // Persist the merged list immediately (rather than clearing it) so a
    // refresh before we even reach the server - still "queued", never
    // confirmed active - survives and gets retried. Only cleared once a real
    // hold is confirmed (below) or restored on any non-success outcome, so
    // the visible count only ever reaches 0 on an actual successful completion.
    setPendingGroupingIds(submissionIds);
    if (groupingAbortRef.current) {
      // Another grouping flow (manual upload, auto-sync batch, or a job
      // discovered on reattach) is already active on this page - only one may
      // run at a time, since a second concurrent call would stomp on the same
      // shared groupingAbortRef/currentGroupingSubmissionIdsRef pair. These
      // ids stay recorded as pending (just persisted above) and get swept
      // into the next runGroupingFlow call once the active one finishes,
      // whether that's a fresh auto-sync batch, a manual retry, or the
      // "Retry now" button.
      console.log(`[UploadLink] runGroupingFlow: event=${eventId} another grouping flow already active - ${submissionIds.length} id(s) queued as pending`);
      return;
    }
    const controller = new AbortController();
    groupingAbortRef.current = controller;
    groupingConfirmedActiveRef.current = false;
    groupingCancelRequestedRef.current = false;
    currentGroupingSubmissionIdsRef.current = submissionIds;
    setGroupingCancelled(false);
    setGroupingDone(false);
    setGroupingActive(true);
    setGroupingQueued(false);
    try {
      const hold = await triggerGroupingWithRetry(eventId, submissionIds, groupingMode, token, () => setGroupingQueued(true), controller.signal);
      setGroupingQueued(false);
      // A real credit hold + kickoff now exist server-side for this event -
      // safe for "Cancel" to call the real cancel-grouping endpoint from here
      // on, and a refresh from here on is handled by the reattach effect
      // (grouping_status + initiated_by_me), so the locally-persisted
      // pending list is no longer needed until/unless this fails below.
      groupingConfirmedActiveRef.current = true;
      setPendingGroupingIds([]);
      await waitForGroupingCompletion(eventId, {
        jobType: hold.grouping_job_type || groupingMode,
        statusFetcher: () => eventApi.getGroupingStatusViaLink(eventId, token),
        signal: controller.signal,
      });
      currentGroupingSubmissionIdsRef.current = [];
      console.log(`[UploadLink] runGroupingFlow: event=${eventId} grouping done for ${submissionIds.length} submission(s)`);
      setGroupingDone(true);
    } catch (err) {
      if (err instanceof GroupingCancelledError) {
        // handleCancelGrouping already did the (local and/or server) cancel
        // work, including requeuing these submission_ids for next time.
        console.log(`[UploadLink] runGroupingFlow: event=${eventId} cancelled`);
        return;
      }
      // Genuine failure (network error, timeout, server-reported grouping
      // failure) - these submission_ids are uploaded but definitely not
      // grouped, so they go back to pending regardless of whether the
      // failure happened while merely queued or after a hold was confirmed.
      console.error(`[UploadLink] runGroupingFlow: event=${eventId} FAILED, requeuing ${submissionIds.length} submission(s):`, err?.message || err);
      setPendingGroupingIds(submissionIds);
      currentGroupingSubmissionIdsRef.current = [];
      throw err;
    } finally {
      groupingAbortRef.current = null;
      groupingConfirmedActiveRef.current = false;
      setGroupingActive(false);
      setGroupingQueued(false);
    }
  }, [canGroup, eventId, groupingMode, token, setPendingGroupingIds]);

  // Cancels whatever grouping wait is currently in flight, regardless of
  // whether it came from this session's own manual upload, its own auto-sync
  // batch, or the reattach effect noticing an already-running job on mount.
  // See groupingConfirmedActiveRef's comment for why the server call is
  // gated: while merely queued/retrying, the active job (if any) belongs to
  // a different session entirely, and cancelling it would be wrong.
  async function handleCancelGrouping() {
    const controller = groupingAbortRef.current;
    if (!controller || groupingCancelRequestedRef.current) return;
    groupingCancelRequestedRef.current = true;
    const shouldCancelServerSide = groupingConfirmedActiveRef.current;
    // Whatever this flow was waiting on (uploaded but not yet grouped) goes
    // back into the pending queue so it's swept into the next attempt -
    // whether that's a new auto-sync batch crossing the threshold again or a
    // manual retry - instead of being silently dropped on cancel. Deduped via
    // Set: while merely queued (never confirmed active), runGroupingFlow has
    // already persisted this same list as "pending", so the two refs are
    // identical at this point and a plain concat would double every id.
    if (currentGroupingSubmissionIdsRef.current.length) {
      setPendingGroupingIds(Array.from(new Set([
        ...pendingGroupingSubmissionIdsRef.current,
        ...currentGroupingSubmissionIdsRef.current,
      ])));
      currentGroupingSubmissionIdsRef.current = [];
    }
    controller.abort();
    if (shouldCancelServerSide) {
      try {
        await eventApi.cancelGroupingViaLink(eventId, token);
      } catch (err) {
        setSnackError(err?.message || 'Failed to cancel grouping.');
      }
    }
    setGroupingCancelled(true);
    setGroupingQueued(false);
    setGroupingActive(false);
  }

  async function handleManualUpload() {
    if (!manualFiles.length || manualBusy) return;
    // Flip manualBusy immediately on click, before the (potentially
    // long-lived, waiting on user interaction) consent dialog - not after it
    // resolves - so auto-sync's isExternallyBusy check and the Sync
    // now/Choose folder buttons' disabled state both reflect "busy" right
    // away instead of leaving a window open for auto-sync to slip in a
    // concurrent cycle while the consent dialog is still up.
    setManualBusy(true);
    setSnackError('');
    const willGroup = canGroup && alsoGroup;
    const isNative = Capacitor.isNativePlatform();
    const jobId = makeUploadJobId(eventId, 'link');
    console.log(`[UploadLink] handleManualUpload: event=${eventId} jobId=${jobId} files=${manualFiles.length} willGroup=${willGroup} isNative=${isNative}`);
    // Set once the native job has actually been handed off - tells the
    // finally block below to leave manualBusy/uploadProgress alone, since
    // watchManualNativeUpload's onDone (not this function returning) is what
    // clears them once the real work is actually done.
    let handedOffToNative = false;
    try {
      if (willGroup) {
        const consentOk = await confirmUploaderGroupingConsent();
        if (!consentOk) return;
      }
      setManualProgress('Uploading photos…');
      setUploadProgress({ phase: 'compressing', completed: 0, total: manualFiles.length });
      if (isNative) {
        // Hands off to CmUploadWorker (WorkManager) - see EventPage.js's
        // startInitialGroupingUpload for the fuller rationale. Grouping-trigger
        // (if willGroup) happens INSIDE CmUploadWorker too, not via the
        // separate runGroupingFlow below, so a job that survives to "upload
        // done" but not to "grouping triggered" (app closed in between) still
        // finishes - nothing is left stored only in this component's state.
        console.log(`[UploadLink] handleManualUpload: event=${eventId} jobId=${jobId} handing off to native CmUploadWorker (upload-link-manual)`);
        const result = await nativeUploadService.startUploadJob({
          jobId, flowKind: 'upload-link-manual', eventId,
          flowMeta: { uploadLinkToken: token, identity, sessionId, canGroup: willGroup, groupingMode },
          totalFiles: manualFiles.length,
          entries: manualFiles.map((file) => ({ relativePath: file.name, file })),
          resumeSource: null,
        });
        if (result?.alreadyActive) {
          // Thrown before clearing manualFiles below, so the picked files stay
          // selected and the user can just retry the same button once the
          // other job (auto-sync, or a previous manual upload) finishes.
          throw new Error('Another upload for this event is already in progress - please wait for it to finish and try again.');
        }
        if (result?.failed) {
          // The native plugin call itself failed - no job was ever created
          // (see nativeUploadService.startUploadJob's own doc comment), so
          // manualFiles must NOT be cleared here (previously always was,
          // regardless of result) - clearing them would silently drop the
          // selection with nothing uploaded and no way to retry short of
          // re-picking the same files.
          throw new Error('Upload failed to start. Please try again.');
        }
        setManualFiles([]);
        // The static compress/upload bar above was only ever a placeholder for
        // this brief hand-off - clear it now and let NativeUploadProgress (fed
        // by watchManualNativeUpload, via resumeJob) take over with real
        // progress instead of leaving it frozen at "Compressing 0/N" for the
        // rest of the job. Seeded synchronously (rather than waiting on
        // watchManualNativeUpload's first async getJobStatus round-trip) so
        // NativeUploadProgress - and the working Cancel button it renders,
        // wired to cancelJob(jobId) - is on screen from this exact render,
        // with no gap where the job is running but nothing on the page can
        // cancel it.
        setManualProgress('');
        setUploadProgress(null);
        setResumeJob({ jobId, flowKind: 'upload-link-manual', eventId, totalFiles: manualFiles.length, completedFiles: 0, phase: 'compressing' });
        handedOffToNative = true;
        watchManualNativeUpload(jobId, {
          beforeUngroupedIds: willGroup ? null : new Set(mySubmissions.filter(s => !s.grouped).map(s => s.submission_id)),
        });
      } else {
        const submissionIds = await submitFiles(manualFiles, setUploadProgress);
        setManualFiles([]);
        setManualProgress('');
        setUploadProgress(null);
        if (willGroup && submissionIds.length) {
          setManualProgress('Starting grouping…');
          await runGroupingFlow(submissionIds);
        } else if (submissionIds.length) {
          // Not grouping yet by choice (alsoGroup off) - queue these ids the
          // same way a cancelled/interrupted grouping attempt is queued, so
          // the "N uploaded photo(s) still need grouping" banner's "Retry now"
          // button becomes this session's explicit "start grouping" action.
          setPendingGroupingIds([...pendingGroupingSubmissionIdsRef.current, ...submissionIds]);
        }
        console.log(`[UploadLink] handleManualUpload: event=${eventId} jobId=${jobId} browser/PWA path done`);
      }
    } catch (err) {
      console.error(`[UploadLink] handleManualUpload: event=${eventId} jobId=${jobId} FAILED:`, err?.message || err);
      setSnackError(err?.message || 'Upload failed. Please try again.');
    } finally {
      if (!handedOffToNative) {
        setManualBusy(false);
        setManualProgress('');
        setUploadProgress(null);
      }
    }
  }

  // Auto-sync: only offered when the link permits triggering grouping - a plain
  // upload-only link has no reason to watch a folder, since nothing beyond the
  // manual picker happens automatically. Namespaced by `${eventId}:${token}` so
  // this session's watch state never collides with the organiser's own EventPage
  // auto-sync (or another link's) in the same browser.
  const handleAutoSyncBatch = useCallback(async (entries, treeUri, onProgress) => {
    // Native: hands off to CmUploadWorker (WorkManager), same as the manual
    // "Upload & Group" button - survives the app being closed mid-upload,
    // shows a real persistent notification, and (via `entries`/`resumeSource`)
    // can resume a large batch from wherever it stopped instead of only
    // recovering an already-compressed tail. Note: unlike organiser auto-sync
    // (EventPage.js), the *detection* of new files for this flow still only
    // runs while the app is open (useLocalAutoSync.js's own poll loop) -
    // AutoSyncCheckWorker's closed-app fallback currently only covers the
    // organiser flows, since it has no notion of a guest's upload-link
    // token/identity/session to act autonomously with. Only the upload
    // *execution* itself is backgroundable here, not the closed-app
    // detection - a known, deliberate scope limit.
    console.log(`[UploadLink] handleAutoSyncBatch: event=${eventId} entries=${entries.length} canGroup=${canGroup} groupingMode=${groupingMode} isNative=${Capacitor.isNativePlatform()}`);
    if (Capacitor.isNativePlatform()) {
      const jobId = makeUploadJobId(eventId, 'link-autosync');
      console.log(`[UploadLink] handleAutoSyncBatch: event=${eventId} jobId=${jobId} handing off to native CmUploadWorker`);
      const willGroup = canGroup && alsoGroup;
      const beforeUngroupedIds = willGroup ? null : new Set(mySubmissions.filter(s => !s.grouped).map(s => s.submission_id));
      setGroupingActive(true);
      try {
        const result = await nativeUploadService.startUploadJob({
          jobId, flowKind: 'upload-link-manual', eventId,
          // canGroup && alsoGroup (not bare canGroup) - mirrors the manual
          // "Upload"/"Upload & Group" button's own willGroup gate below, so
          // auto-sync respects the same per-session grouping choice instead
          // of always grouping whenever the link permits it.
          flowMeta: { uploadLinkToken: token, identity, sessionId, canGroup: canGroup && alsoGroup, groupingMode },
          totalFiles: entries.length,
          entries,
          resumeSource: treeUri ? { kind: 'saf', treeUri } : null,
        });
        if (result?.alreadyActive) {
          // Another job (the manual button, or a previous auto-sync batch still
          // finishing) is already running for this event - these entries were
          // never staged anywhere, so treating this as success would silently
          // drop them from the pending queue with nothing ever uploaded (see
          // nativeUploadService.startUploadJob's own doc comment). Shaped like
          // a lock-busy 409 so useLocalAutoSync's resolveAndSubmit requeues
          // this batch and retries it automatically shortly, the same as a
          // genuine grouping-lock contention.
          console.log(`[UploadLink] handleAutoSyncBatch: event=${eventId} jobId=${jobId} another job already active - will retry shortly`);
          const err = new Error('Another upload for this event is already active - will retry shortly.');
          err.data = { retryable: true };
          throw err;
        }
        console.log(`[UploadLink] handleAutoSyncBatch: event=${eventId} jobId=${jobId} native job started (see CmUploadWorker/CmApiClient logs for the rest)`);
        // Seeded synchronously - see the matching comment in handleManualUpload
        // - so NativeUploadProgress's Cancel button is on screen immediately,
        // not only once watchNativeJobAndRefresh's first async poll lands.
        setResumeJob({ jobId, flowKind: 'upload-link-manual', eventId, totalFiles: entries.length, completedFiles: 0, phase: 'compressing' });
        watchNativeJobAndRefresh(jobId, { beforeUngroupedIds });
      } finally {
        setGroupingActive(false);
      }
      return;
    }
    // Browser/PWA: unchanged - resolve everything up front and submit via the
    // same manual-upload path, since uploadPhotoSubmissionsViaLink has no
    // incremental-compress support there. `onProgress` (from
    // useLocalAutoSync's resolveAndSubmit) drives the OS/PWA notification's
    // "N / M photos compressed"/"uploaded" text - the native branch above
    // doesn't need it, since CmUploadWorker reports its own progress directly.
    console.log(`[UploadLink] handleAutoSyncBatch: event=${eventId} browser/PWA path, resolving ${entries.length} file(s)`);
    const files = await Promise.all(entries.map((e) => (e.file ? e.file : e.resolveFile())));
    const submissionIds = await submitFiles(files, onProgress);
    if (!submissionIds.length) return;
    // alsoGroup is this session's explicit choice (same toggle the manual
    // button uses) - when off, these submission_ids stay pending (tracked via
    // setPendingGroupingIds below) until the visitor explicitly starts
    // grouping, instead of always auto-grouping whenever the link permits it.
    if (!canGroup || !alsoGroup) {
      setPendingGroupingIds([...pendingGroupingSubmissionIdsRef.current, ...submissionIds]);
      return;
    }
    await runGroupingFlow(submissionIds);
  }, [submitFiles, canGroup, alsoGroup, mySubmissions, runGroupingFlow, eventId, token, identity, sessionId, groupingMode, watchNativeJobAndRefresh, setPendingGroupingIds]);

  // Reattach on mount/refresh: if a job is already genuinely running for this
  // event (this session's own earlier action before a refresh, or literally
  // anyone else's - the lock and this check are both per-event), pick up its
  // real progress instead of the page defaulting to a plain idle "watching"
  // view that quietly has no idea anything is happening. Only runs once
  // canGroup is known, since grouping-status now requires the same "logged-in
  // CircleMoments account" the rest of the grouping surface requires.
  useEffect(() => {
    if (!canGroup || !eventId) return undefined;
    let cancelled = false;
    eventApi.getGroupingStatusViaLink(eventId, token).then((res) => {
      if (cancelled) return;
      const jobActive = res.status === 'processing' || res.status === 'uploading' || res.status === 'starting';
      console.log(`[UploadLink] mount-reattach: event=${eventId} status=${res.status} initiatedByMe=${res.initiated_by_me} jobActive=${jobActive} pendingCarriedOver=${pendingGroupingSubmissionIdsRef.current.length}`);
      // A job that's active but wasn't started by this same account isn't
      // ours to watch or cancel - the server would reject a cancel attempt
      // anyway (see cancel_grouping_proxy's ownership check), so surfacing
      // "grouping in progress" with a live Cancel button here would just be
      // a misleading no-op control for someone else's job (the organiser's
      // own direct-dashboard run, or another visitor's).
      if (!jobActive || res.initiated_by_me === false) {
        // Nothing of ours is actively running server-side. If photos are
        // still waiting on a grouping trigger from an earlier visit to this
        // page (queued but never confirmed, or confirmed and later failed -
        // see pendingGroupingSubmissionIdsRef), resume that automatically
        // instead of leaving them stranded until the user happens to upload
        // something new or notices a manual retry affordance. Safe even if
        // jobActive is true for someone else's job: triggerGroupingWithRetry
        // just queues on the same retryable 409 as any other contention.
        if (!groupingAbortRef.current && pendingGroupingSubmissionIdsRef.current.length) {
          runGroupingFlow([]);
        }
        return;
      }
      // A user-initiated trigger (manual upload or auto-sync) already started
      // and is being tracked - defer to it rather than clobbering its cancel
      // button with this passively-observed one.
      if (groupingAbortRef.current) return;
      const controller = new AbortController();
      groupingAbortRef.current = controller;
      groupingConfirmedActiveRef.current = true;
      setGroupingActive(true);
      // This is confirmed to be our own active job (initiated_by_me passed
      // above) - whatever was recorded as still-pending is now covered by it,
      // so drop the redundant "N still pending" display while it's tracked.
      // If it later fails, the persisted pending list simply isn't restored
      // here (we don't know the exact ids for a job discovered on reattach,
      // only that it's ours) - an acceptable gap next to the far more common
      // path (this tab's own runGroupingFlow) which does restore correctly.
      setPendingGroupingIds([]);
      waitForGroupingCompletion(eventId, {
        jobType: res.grouping_job_type || groupingMode,
        statusFetcher: () => eventApi.getGroupingStatusViaLink(eventId, token),
        signal: controller.signal,
      }).then(() => {
        if (!cancelled) setGroupingDone(true);
      }).catch((err) => {
        if (!cancelled && !(err instanceof GroupingCancelledError)) {
          setSnackError(err?.message || 'Grouping failed.');
        }
      }).finally(() => {
        if (cancelled) return;
        groupingAbortRef.current = null;
        groupingConfirmedActiveRef.current = false;
        setGroupingActive(false);
      });
    }).catch(() => {});
    // Stop this page instance's own polling on unmount - deliberately does NOT
    // call cancelGroupingViaLink, since navigating away shouldn't cancel a
    // genuinely-in-progress billed job, only this tab's tracking of it.
    return () => { cancelled = true; groupingAbortRef.current?.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canGroup, eventId]);

  const autoSync = useLocalAutoSync({
    eventId,
    storageKey: eventId ? `${eventId}:${token}` : undefined,
    onBatchReady: handleAutoSyncBatch,
    onReattach: requeueOnUploadLinkReattach,
    // Pushed to native (see useLocalAutoSync.js's nativeTargetMeta) so
    // AutoSyncTrigger.kt can act as this exact guest submitter - the same
    // token/identity/session/consent this page itself submits with - while
    // the app is fully closed, via a real "upload-link-manual" WorkManager job.
    nativeTargetMeta: { uploadLinkToken: token, identity, sessionId, canGroup, groupingMode },
    // Keep the manual-upload pipeline and auto-sync's own submit-and-group
    // cycle mutually exclusive end-to-end. manualBusy covers the (non-native)
    // manual compress/upload phase; groupingActive covers a foreground-tracked
    // grouping wait; resumeJob covers a native CmUploadWorker job for this
    // event, whether started by the manual button or auto-sync itself -
    // native's own startUploadJob returns almost immediately (the real work
    // continues in WorkManager), so groupingActive alone no longer spans a
    // native job's actual duration the way it used to.
    isExternallyBusy: () => manualBusy || groupingActive || Boolean(resumeJob),
  });

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
        <Container maxWidth="sm">
          <Skeleton variant="text" width={260} height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={180} height={24} sx={{ mb: 3 }} />
          <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 6 }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <Typography variant="h5" fontWeight={700} mb={2}>Upload link unavailable</Typography>
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
          <Button variant="contained" onClick={() => navigate('/')}>Go to CircleMoments</Button>
          <StrandedAutoSyncStopButton token={token} />
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ background: 'linear-gradient(135deg, #fff4f8 0%, #fff8ef 100%)', borderBottom: '1px solid', borderColor: 'divider', py: 2.5, px: { xs: 2, sm: 3 } }}>
        <Container maxWidth="sm">
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            CircleMoments
          </Typography>
          <Typography variant="h5" fontWeight={800} color="#1a237e">
            {data?.event_name || 'Upload photos'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {canGroup
              ? 'Upload your photos - the organiser will be credited for grouping them into the gallery.'
              : 'Upload your photos for the organiser to add to the event gallery.'}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="sm" sx={{ py: 3 }}>
        {!detailsConfirmed ? (
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }} component="form" onSubmit={confirmDetails}>
            <Typography variant="subtitle1" fontWeight={700} mb={0.5}>Your details</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {user ? 'Pre-filled from your account - update if needed.' : 'So the organiser knows who shared these photos.'}
            </Typography>
            <Stack spacing={1.5}>
              <TextField label="Full name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth size="small" />
              <TextField label="Email address" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required fullWidth size="small" />
              <TextField label="Phone number (optional)" value={phone} onChange={(e) => setPhone(e.target.value)} fullWidth size="small" />
              <FormControlLabel
                control={<Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} required />}
                label={<Typography variant="body2" color="text.secondary">I agree that CircleMoments may store the photos I upload here for this event.</Typography>}
              />
              <Button type="submit" variant="contained" disabled={!identity.name || !identity.email || !consent} sx={{ textTransform: 'none' }}>
                Continue
              </Button>
            </Stack>
          </Paper>
        ) : (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" fontWeight={700}>{identity.name}</Typography>
                <Typography variant="caption" color="text.secondary">{identity.email}</Typography>
              </Box>
              <Button size="small" onClick={() => setDetailsConfirmed(false)} sx={{ textTransform: 'none' }}>Edit</Button>
            </Paper>

            {snackError && <Alert severity="error" onClose={() => setSnackError('')}>{snackError}</Alert>}

            {linkAllowsGrouping && !user && (
              <Alert severity="info" action={<Button size="small" onClick={() => navigate('/auth')} sx={{ textTransform: 'none' }}>Sign in</Button>}>
                This link can also trigger grouping - sign in with any CircleMoments account to enable it.
              </Alert>
            )}

            {/* Covers all three ways grouping can be active: this session's own
                manual upload, its own auto-sync batch, or a job discovered
                already running on mount/refresh (see the reattach effect) -
                one status block + cancel button regardless of origin. */}
            {groupingQueued && (
              <Alert
                severity="info"
                action={<Button size="small" onClick={handleCancelGrouping} sx={{ textTransform: 'none' }}>Cancel</Button>}
              >
                Another upload is grouping right now - yours is queued and will retry automatically.
              </Alert>
            )}
            {groupingActive && !groupingQueued && (
              <Alert
                severity="info"
                action={<Button size="small" onClick={handleCancelGrouping} sx={{ textTransform: 'none' }}>Cancel</Button>}
              >
                Grouping is in progress…
              </Alert>
            )}
            {groupingCancelled && (
              <Alert severity="info" onClose={() => setGroupingCancelled(false)}>Grouping cancelled.</Alert>
            )}
            {groupingDone && (
              <Alert severity="success" onClose={() => setGroupingDone(false)}>Grouping complete - your photos are in the gallery.</Alert>
            )}

            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                <CloudUploadIcon color="primary" />
                <Typography variant="subtitle1" fontWeight={700}>Upload photos</Typography>
                {mySubmissions.length > 0 && <Chip size="small" color="success" icon={<CheckCircleIcon />} label={`${mySubmissions.length} uploaded`} />}
              </Stack>
              <NativeUploadProgress
                job={resumeJob}
                onCancel={async () => {
                  // A native job has no live component holding any JS-side
                  // abort handle for it - cancelJob is the only thing that
                  // can actually stop it.
                  await nativeUploadService.cancelJob({ jobId: resumeJob.jobId }).catch(() => {});
                  // cancelGroupingViaLink is an authenticated, grouping-actor-
                  // only endpoint (see UploadLinkPage.js's canGroup) - calling
                  // it for an anonymous visitor, or on a plain upload-only
                  // link where grouping was never possible, has no token to
                  // send and 401s. That 401 isn't just swallowed: api.js's
                  // global response interceptor treats ANY 401 from ANY
                  // endpoint as "session expired" and redirects to /auth -
                  // confirmed on-device as this exact Cancel button bouncing
                  // an anonymous upload-only visitor off the page entirely.
                  // Guarded on canGroup, same gate the rest of this page uses
                  // before ever touching a grouping endpoint.
                  if (canGroup) {
                    try {
                      await eventApi.cancelGroupingViaLink(eventId, token);
                    } catch {
                      // Local state still clears below; backend cleanup may already be complete.
                    }
                  }
                  setResumeJob(null);
                }}
              />
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button component="label" variant="outlined" sx={{ textTransform: 'none' }}>
                  Choose photos
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={(e) => setManualFiles(Array.from(e.target.files || []))}
                  />
                </Button>
                <Tooltip title={(canGroup && alsoGroup && !manualBusy && autoSync.status === 'submitting') ? 'Auto-sync is uploading/grouping right now - wait for it to finish.' : ''}>
                  <span>
                    <Button
                      variant="contained"
                      // Only contends with auto-sync's grouping when this click will
                      // itself trigger grouping (canGroup && alsoGroup) - a plain
                      // upload-only click never calls runGroupingFlow, so it can
                      // proceed freely even while auto-sync's own grouping is active.
                      disabled={!manualFiles.length || manualBusy || Boolean(resumeJob) || (canGroup && alsoGroup && (groupingActive || autoSync.status === 'submitting'))}
                      onClick={handleManualUpload}
                      startIcon={manualBusy ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      {manualBusy ? (manualProgress || 'Uploading…') : (canGroup && alsoGroup ? 'Upload & Group' : 'Upload')}
                    </Button>
                  </span>
                </Tooltip>
                {manualFiles.length > 0 && !manualBusy && (
                  <Typography variant="body2" color="text.secondary">{manualFiles.length} file(s) selected</Typography>
                )}
              </Stack>
              {canGroup && (
                <FormControlLabel
                  sx={{ mt: 0.5 }}
                  control={<Switch size="small" checked={alsoGroup} onChange={(e) => setAlsoGroup(e.target.checked)} />}
                  label={<Typography variant="body2" color="text.secondary">Also trigger grouping</Typography>}
                />
              )}
              {uploadProgress && uploadProgress.total > 0 && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(uploadProgress.completed / uploadProgress.total) * 100}
                    sx={{ borderRadius: 1, height: 6 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {uploadProgress.phase === 'compressing' ? 'Compressing' : 'Uploading'}{' '}
                    {uploadProgress.completed}/{uploadProgress.total}
                  </Typography>
                  {shouldShowUploadWarning() ? (
                    <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>{UPLOAD_IN_PROGRESS_WARNING}</Alert>
                  ) : shouldShowBackgroundableNotice() ? (
                    <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>{UPLOAD_BACKGROUNDABLE_NOTICE}</Alert>
                  ) : null}
                </Box>
              )}
              {mySubmissions.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Your uploads ({mySubmissions.length})
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: 1 }}>
                    {mySubmissions.slice(0, visibleSubmissionsCount).map((s) => (
                      thumbUrls[s.submission_id] ? (
                        <Box
                          key={s.submission_id}
                          component="img"
                          src={thumbUrls[s.submission_id]}
                          alt={s.original_filename || 'Uploaded photo'}
                          loading="lazy"
                          sx={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 1, bgcolor: 'grey.100' }}
                        />
                      ) : (
                        <Skeleton key={s.submission_id} variant="rectangular" sx={{ width: '100%', aspectRatio: '1', borderRadius: 1 }} />
                      )
                    ))}
                  </Box>
                  {visibleSubmissionsCount < mySubmissions.length && (
                    <Button
                      size="small"
                      onClick={() => setVisibleSubmissionsCount((c) => c + SUBMISSIONS_PAGE_SIZE)}
                      sx={{ textTransform: 'none', mt: 1 }}
                    >
                      Load {Math.min(SUBMISSIONS_PAGE_SIZE, mySubmissions.length - visibleSubmissionsCount)} more
                      ({mySubmissions.length - visibleSubmissionsCount} remaining)
                    </Button>
                  )}
                </Box>
              )}
            </Paper>

            {
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                  <AutoAwesomeIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight={700}>Auto-sync a folder</Typography>
                  {autoSync.armed && <Chip size="small" color="primary" label={autoSync.status} />}
                </Stack>
                {!autoSync.supported ? (
                  <Typography variant="body2" color="text.secondary">Folder auto-sync isn't supported in this browser.</Typography>
                ) : !autoSync.armed ? (
                  <>
                    <Typography variant="body2" color="text.secondary" mb={1.5}>
                      {canGroup
                        ? 'Watch a folder on this device - new photos are uploaded and grouped automatically as they appear.'
                        : 'Watch a folder on this device - new photos are uploaded automatically as they appear.'}
                    </Typography>
                    <ScanProgressBar autoSync={autoSync} />
                    <Tooltip title={(groupingActive || manualBusy) ? 'Wait for the current upload/grouping to finish before starting another.' : ''}>
                      <span>
                        <Button
                          variant="outlined"
                          startIcon={<FolderIcon />}
                          disabled={autoSync.scanning || groupingActive || manualBusy}
                          onClick={async () => {
                            // Biometric-processing consent only makes sense when this
                            // link/session can actually trigger grouping - a plain
                            // upload-only auto-sync never sends anything through face
                            // detection, so there's nothing here to consent to.
                            if (canGroup) {
                              const consentOk = await confirmUploaderGroupingConsent();
                              if (!consentOk) return;
                            }
                            autoSync.selectFolder(promptAutoSyncIncludeExisting, promptStopOtherAutoSync);
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          {autoSync.scanning ? 'Reading folder…' : 'Choose folder'}
                        </Button>
                      </span>
                    </Tooltip>
                  </>
                ) : autoSync.needsReconnect ? (
                  <Alert severity="warning">
                    <Stack spacing={0.75} alignItems="flex-start">
                      <span>Folder access was lost - reconnect to keep syncing.</span>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" onClick={autoSync.reconnect}>Reconnect</Button>
                        <Button
                          size="small"
                          sx={{ opacity: 0.7 }}
                          onClick={async () => {
                            const ok = await swalConfirmDelete({
                              title: 'Reset auto-sync data?',
                              text: "This clears the local sync state for every event on this device, not just this one - you'll need to re-choose any watched folders afterwards.",
                              confirmText: 'Reset',
                            });
                            if (ok) await autoSync.hardReset();
                          }}
                        >
                          Reset
                        </Button>
                      </Stack>
                    </Stack>
                  </Alert>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      Watching <strong>{autoSync.folderName}</strong> - {autoSync.pendingCount} photo(s) pending.
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">Batch size:</Typography>
                      <TextField
                        size="small"
                        value={autoSync.batchSizeSetting}
                        onChange={(e) => autoSync.setBatchSizeSetting(e.target.value.replace(/[^\d]/g, ''))}
                        inputProps={{ inputMode: 'numeric', style: { padding: '2px 8px', width: 56, textAlign: 'center' } }}
                        disabled={autoSync.status === 'submitting'}
                      />
                    </Stack>
                    <ScanProgressBar autoSync={autoSync} />
                    {/* Same grouping status/cancel as the top-of-page block, repeated
                        here since auto-sync batches are the most common way a
                        grouping run gets started and users shouldn't have to scroll
                        up to see or cancel it. */}
                    {groupingQueued && (
                      <Alert
                        severity="info"
                        sx={{ mb: 1 }}
                        action={<Button size="small" onClick={handleCancelGrouping} sx={{ textTransform: 'none' }}>Cancel</Button>}
                      >
                        Another upload is grouping right now - yours is queued and will retry automatically.
                      </Alert>
                    )}
                    {groupingActive && !groupingQueued && (
                      <Alert
                        severity="info"
                        sx={{ mb: 1 }}
                        action={<Button size="small" onClick={handleCancelGrouping} sx={{ textTransform: 'none' }}>Cancel</Button>}
                      >
                        Grouping is in progress…
                      </Alert>
                    )}
                    {!groupingActive && pendingGroupingCount > 0 && (
                      <Alert
                        severity="warning"
                        sx={{ mb: 1 }}
                        action={
                          <Button size="small" onClick={() => runGroupingFlow([])} sx={{ textTransform: 'none' }}>
                            Retry now
                          </Button>
                        }
                      >
                        {pendingGroupingCount} uploaded photo(s) still need grouping.
                      </Alert>
                    )}
                    {resumeJob ? (
                      // Native (CmUploadWorker) is actively running this batch - see
                      // NativeUploadProgress's own doc comment for why autoSync.status/
                      // pendingCount alone can't show this: both settle back to their
                      // resting values the moment the job is handed off to WorkManager,
                      // well before the real compress/upload work is actually done.
                      <NativeUploadProgress job={resumeJob} />
                    ) : (autoSync.status === 'submitting') && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}
                    {autoSync.lastError && <Alert severity="info" sx={{ mb: 1 }}>{autoSync.lastError}</Alert>}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Tooltip title={(groupingActive || manualBusy) ? 'Wait for the current upload/grouping to finish before starting another.' : ''}>
                        <span>
                          <Button size="small" onClick={autoSync.syncNow} disabled={!autoSync.pendingCount || autoSync.scanning || groupingActive || manualBusy} sx={{ textTransform: 'none' }}>Sync now</Button>
                        </span>
                      </Tooltip>
                      <Button size="small" color="inherit" onClick={autoSync.stopSync} sx={{ textTransform: 'none' }}>Stop watching</Button>
                      <Button
                        size="small"
                        color="inherit"
                        onClick={async () => {
                          const ok = await swalConfirmDelete({
                            title: 'Reset auto-sync data?',
                            text: "This clears the local sync state for every event on this device, not just this one - you'll need to re-choose any watched folders afterwards.",
                            confirmText: 'Reset',
                          });
                          if (ok) await autoSync.hardReset();
                        }}
                        sx={{ textTransform: 'none', opacity: 0.6 }}
                      >
                        Reset auto-sync data
                      </Button>
                    </Stack>
                  </>
                )}
              </Paper>
            }
          </Stack>
        )}
      </Container>

      <Box sx={{ py: 3, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider', mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Powered by{' '}
          <Box component="a" href="https://www.circlemoments.com" target="_blank" rel="noopener noreferrer"
            sx={{ color: '#C73866', textDecoration: 'none', fontWeight: 700 }}>
            CircleMoments
          </Box>
        </Typography>
      </Box>
    </Box>
  );
}
